import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useState } from 'react';

import { ChatContainer } from '@/components/ChatContainer';
import { ScreenLoader } from '@/components/global/Loader';
import { SidebarLayout } from '@/components/layout/SidebarLayout';
import { AuthenticatedUserProvider } from '@/components/providers/AuthenticatedUserProvider';
import { type Chat, ChatProvider } from '@/components/providers/ChatProvider';
import { FeatureFlagsProvider } from '@/components/providers/FeatureFlagsProvider';
import { WebSocketsProvider } from '@/components/providers/WebsocketsProvider';
import { preLoadUserAndSession, userQueryOptions } from '@/lib/queries/auth';
import { projectQueries } from '@/lib/queries/project';
import { userQueries } from '@/lib/queries/user';

export const Route = createFileRoute('/_root')({
  component: RouteComponent,
  beforeLoad: ({ context: { formatMessage } }) => ({
    title: formatMessage({ id: 'global.breadcrumb.home' }),
  }),
  loader: preLoadUserAndSession,
  pendingComponent: ScreenLoader,
});

function RouteComponent() {
  const { user, session } = Route.useLoaderData();
  const { data: userData } = useQuery(userQueries.current());
  useSuspenseQuery(userQueryOptions);
  const [chat, setChat] = useState<Chat>({
    messages: [],
    thinking: undefined,
  });
  const { data: defaultProject } = useQuery(projectQueries.default());

  return (
    <AuthenticatedUserProvider user={user} session={session}>
      <WebSocketsProvider>
        <FeatureFlagsProvider>
          <SidebarLayout isSuperUser={userData?.superuser ?? false}>
            <ChatProvider setChat={setChat} chat={chat}>
              <Outlet />
              {defaultProject?.defaultProject !== undefined && (
                <ChatContainer
                  chat={chat}
                  defaultProjectId={defaultProject.defaultProject.id}
                />
              )}
            </ChatProvider>
          </SidebarLayout>
        </FeatureFlagsProvider>
      </WebSocketsProvider>
    </AuthenticatedUserProvider>
  );
}
